import React from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../layouts/DashboardLayout';
import {
  DiscoverTrip,
  discoverTrips,
  extractTripUuid,
} from '../../../services/trips';
import { getPublicProfile, PublicProfile } from '../../../services/social/api';
import { coverFor } from '../../utils/tripCover';
import './ExplorePage.css';

/**
 * Explore — browse and search PUBLIC trips (BETA-3, closes GAP-28).
 *
 * This page used to render a hardcoded `data/exploreTrips.ts` array with
 * filters (country / city / budget tier / traveler group / tags) that the real
 * data cannot supply, a "Based on your trips" recommendation rail that does not
 * exist yet (T2.10r), a public **save count** that D-25 forbids outright, and
 * cards that only scrolled to themselves instead of opening the trip.
 *
 * What replaced it is narrower on purpose: everything here is a field the
 * server actually returns. Destination and sort are derived from real results;
 * search hits the same endpoint as browse. Nothing is promised that isn't real.
 */

type SortKey = 'newest' | 'most-liked';

const SORTS: { value: SortKey; label: string }[] = [
  { value: 'newest', label: 'Newest' },
  { value: 'most-liked', label: 'Most liked' },
];

/** Author names, resolved once and shared across cards (same idiom as the feed). */
const nameCache = new Map<string, PublicProfile | null>();

const displayName = (profile: PublicProfile | null | undefined): string => {
  if (!profile) return 'Traveler';
  const full = [profile.firstName, profile.lastName].filter(Boolean).join(' ').trim();
  return full || profile.nickname || 'Traveler';
};

/** "9 days" from the trip's own dates — the only duration we can honestly show. */
const durationOf = (trip: DiscoverTrip): string | null => {
  const start = trip.dates?.startDate;
  const end = trip.dates?.endDate;
  if (!start || !end) return null;
  const ms = Date.parse(end) - Date.parse(start);
  if (!Number.isFinite(ms) || ms < 0) return null;
  const days = Math.round(ms / 86_400_000) + 1;
  return days === 1 ? '1 day' : `${days} days`;
};

const ExplorePage: React.FC = () => {
  const navigate = useNavigate();

  const [query, setQuery] = React.useState('');
  const [submitted, setSubmitted] = React.useState('');
  const [destination, setDestination] = React.useState<string | null>(null);
  const [sort, setSort] = React.useState<SortKey>('newest');

  const [trips, setTrips] = React.useState<DiscoverTrip[]>([]);
  const [cursor, setCursor] = React.useState<string | undefined>(undefined);
  const [loading, setLoading] = React.useState(true);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [profiles, setProfiles] = React.useState<Record<string, PublicProfile | null>>({});

  /** One lookup per DISTINCT owner, and only for names we don't already hold. */
  const resolveOwners = React.useCallback(async (rows: DiscoverTrip[]) => {
    const missing = Array.from(new Set(rows.map((row) => row.ownerId)))
      .filter((id) => id && !nameCache.has(id));
    if (missing.length === 0) return;
    await Promise.all(missing.map(async (id) => {
      nameCache.set(id, await getPublicProfile(id).catch(() => null));
    }));
    setProfiles(Object.fromEntries(nameCache));
  }, []);

  // Load whenever the SUBMITTED query changes (browse when it is empty).
  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const page = await discoverTrips(submitted || undefined);
        if (cancelled) return;
        setTrips(page.trips);
        setCursor(page.nextCursor);
        setError(null);
        await resolveOwners(page.trips);
      } catch {
        if (!cancelled) setError('Could not load trips. Please try again.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [submitted, resolveOwners]);

  const loadMore = async () => {
    if (!cursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const page = await discoverTrips(submitted || undefined, cursor);
      const seen = new Set(trips.map((trip) => trip.id));
      const next = [...trips, ...page.trips.filter((trip) => !seen.has(trip.id))];
      setTrips(next);
      setCursor(page.nextCursor);
      await resolveOwners(page.trips);
    } catch {
      // The trips already on screen stay valid; the button remains for a retry.
    } finally {
      setLoadingMore(false);
    }
  };

  // Destinations offered as filters come from the RESULTS, so the list can
  // never advertise a place with nothing behind it.
  const destinations = React.useMemo(
    () => Array.from(new Set(trips.flatMap((trip) => trip.destinations || []).filter(Boolean))).sort(),
    [trips],
  );

  const visible = React.useMemo(() => {
    const filtered = destination
      ? trips.filter((trip) => (trip.destinations || []).includes(destination))
      : trips;
    const sorted = [...filtered];
    if (sort === 'most-liked') {
      sorted.sort((a, b) => (b.likes ?? 0) - (a.likes ?? 0));
    } else {
      sorted.sort((a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || '')));
    }
    return sorted;
  }, [trips, destination, sort]);

  const openTrip = (trip: DiscoverTrip) => {
    navigate(`/trips/${extractTripUuid(trip.id)}`, { state: { backTo: '/explore' } });
  };

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    setDestination(null);
    setSubmitted(query.trim());
  };

  const clear = () => {
    setQuery('');
    setDestination(null);
    setSubmitted('');
  };

  const isFiltered = Boolean(submitted || destination);

  return (
    <DashboardLayout activeSection="explore" wide>
      <div className="v2-explore-page">
        <header className="v2-explore-page__hero">
          <h1>Find your next trip</h1>
          <p>Real itineraries from real travelers — browse, search, and clone what fits.</p>
        </header>

        <section className="v2-explore-page__community" aria-labelledby="explore-community-heading">
          <div className="v2-explore-page__section-head">
            <h2 id="explore-community-heading">
              {submitted ? `Results for “${submitted}”` : 'Public trips'}
            </h2>
            {!loading && <span>{visible.length} {visible.length === 1 ? 'trip' : 'trips'}</span>}
          </div>

          <div className="v2-explore-filters">
            <form className="v2-explore-filters__search" onSubmit={submit} role="search">
              <input
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search trips by title or destination…"
                aria-label="Search public trips"
              />
              <button type="submit">Search</button>
            </form>

            <div className="v2-explore-filters__dropdowns">
              {destinations.length > 0 && (
                <label className="v2-explore-filter__select">
                  <span>Destination</span>
                  <select
                    value={destination || ''}
                    onChange={(event) => setDestination(event.target.value || null)}
                  >
                    <option value="">All destinations</option>
                    {destinations.map((place) => (
                      <option key={place} value={place}>{place}</option>
                    ))}
                  </select>
                </label>
              )}

              <label className="v2-explore-filter__select">
                <span>Sort</span>
                <select value={sort} onChange={(event) => setSort(event.target.value as SortKey)}>
                  {SORTS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </label>

              {isFiltered && (
                <button type="button" className="v2-explore-filters__clear" onClick={clear}>
                  Clear
                </button>
              )}
            </div>
          </div>

          {loading ? (
            <p className="v2-explore-page__status">Loading trips…</p>
          ) : error ? (
            <div className="v2-explore-page__empty">
              <h3>Couldn’t load trips</h3>
              <p>{error}</p>
              <button type="button" onClick={() => setSubmitted((current) => current)}>Try again</button>
            </div>
          ) : visible.length === 0 ? (
            <div className="v2-explore-page__empty">
              {/* The two empty cases are genuinely different and must not share
                  copy: "nobody has published yet" is not "your search missed". */}
              {isFiltered ? (
                <>
                  <h3>No trips match that</h3>
                  <p>Try a different destination, or clear the search to browse everything public.</p>
                  <button type="button" onClick={clear}>Clear search</button>
                </>
              ) : (
                <>
                  <h3>No public trips yet</h3>
                  <p>
                    Trips show up here once someone sets one to public. Make one of yours public and
                    it’ll be the first.
                  </p>
                  <button type="button" onClick={() => navigate('/trips')}>Go to my trips</button>
                </>
              )}
            </div>
          ) : (
            <>
              <div className="v2-explore-page__grid">
                {visible.map((trip) => {
                  const duration = durationOf(trip);
                  const where = (trip.destinations || []).join(', ');
                  return (
                    <button
                      type="button"
                      key={trip.id}
                      className="v2-explore-trip-card"
                      onClick={() => openTrip(trip)}
                      aria-label={`Open ${trip.title}`}
                    >
                      <div className="v2-explore-trip-card__media">
                        <img src={coverFor(trip.id)} alt="" />
                        {(trip.likes ?? 0) > 0 && (
                          <span className="v2-explore-trip-card__likes">♥ {trip.likes}</span>
                        )}
                      </div>
                      <div className="v2-explore-trip-card__body">
                        <h3>{trip.title}</h3>
                        <div className="v2-explore-trip-card__author">
                          {profiles[trip.ownerId]?.pictureUrl && (
                            <img src={profiles[trip.ownerId]?.pictureUrl || undefined} alt="" />
                          )}
                          <span>{displayName(profiles[trip.ownerId])}</span>
                        </div>
                        {where && <p>{where}</p>}
                        <div className="v2-explore-trip-card__meta">
                          {duration && <span>{duration}</span>}
                          {trip.status && <span>{trip.status}</span>}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>

              {cursor && (
                <button
                  type="button"
                  className="v2-explore-page__more"
                  onClick={() => { void loadMore(); }}
                  disabled={loadingMore}
                >
                  {loadingMore ? 'Loading…' : 'Load more trips'}
                </button>
              )}
            </>
          )}
        </section>
      </div>
    </DashboardLayout>
  );
};

export default ExplorePage;
